import {
  ToDoCommentDTO,
  ToDoCommentsCollectionDTO,
  ToDoPaginatedCollectionDTO,
  ToDoPanelItemDTO,
  UserDTO,
} from 'dtos';
import {
  FilterLookupTranslator,
} from 'core/Filters/types';
import {
  LookupOptionType,
} from 'core/components/Lookup/Generic';
import {
  formatDate,
  formatDateTime,
} from 'utils/date';
import {
  User,
  UserCompact,
  UserLike,
} from 'types/user';
import {
  ToDo,
  ToDoStatus,
  CommentActivity,
} from 'types/toDo';
import {
  Patient,
} from 'pages/Dashboard/types/patient';
import {
  normalizeContentForTextarea,
} from 'views/EMR/Settings/helper';
import {
  getDisplayName,
} from 'pages/Dashboard/utils/helper';
import isNil from 'lodash/isNil';

export const statusLabels: Record<string, string> = Object.freeze({
  NotStarted: 'Not Started',
  InProgress: 'In Progress',
  Completed: 'Completed',
  Abandon: 'Abandoned',
  Abandoned: 'Abandoned',
});

const transitions: Record<string, string> = Object.freeze({
  NotStarted: 'NotStarted',
  Completed: 'Completed',
  InProgress: 'InProgress',
  Abandoned: 'Abandon',
});

export function normalizeCurrentToDoStatus(status: string): ToDoStatus {
  return ({
    transitionName: transitions[status],
    statusDisplayName: statusLabels[status],
  });
}

export function normalizeValidToDoStatuses(statuses: string[]): ToDoStatus[] {
  return statuses.map((status) => ({
    transitionName: transitions[status],
    statusDisplayName: statusLabels[status],
  }));
}

export function normalizeAvailableToDoStatuses(
  currentStatus: string,
  availableStatuses: ToDoStatus[],
): ToDoStatus[] {
  return [normalizeCurrentToDoStatus(currentStatus)]
    .concat(
      availableStatuses
        .filter((status) => status.transitionName !== 'Abandon')
        .map((status) => ({
          transitionName: status.transitionName,
          statusDisplayName: statusLabels[status.statusDisplayName],
        })),
    );
}

function normalizeUser(user: UserLike): UserCompact {
  return {
    id: user?.userId ?? user?.patientId ?? 0,
    fullName: getDisplayName(user?.formattedNames),
    formattedNames: user?.formattedNames ?? {},
    image: null,
  };
}

function normalizePatient(patient: Patient): Patient {
  return {
    ...patient,
    patientId: patient?.patientId ?? 0,
  };
}

export function normalizeCommentActivity(
  commentsResponse: ToDoCommentsCollectionDTO,
): CommentActivity[] {
  const { commentActivity: data, users } = commentsResponse;
  return (data ?? [])
    .map((comment: ToDoCommentDTO) => ({
      ...comment,
      comment: normalizeContentForTextarea(comment?.comment ?? ''),
      creator: normalizeUser(users?.[comment?.creatorUserId ?? 0] ?? {}),
      formattedDate: formatDateTime(comment?.createdOn ?? ''),
      toDoCommentId: comment?.toDoCommentId ?? 0,
      toDoId: comment?.toDoId ?? 0,
      creatorUserId: comment?.creatorUserId ?? 0,
      createdOn: comment?.createdOn ?? '',
    }));
}

export function normalizeToDo(
  data: ToDo,
  userDict?: Record<number, User | UserDTO>,
  patientsDict?: Record<number, Patient>,
): ToDo {
  const assignedUserIds = data.assigneeUserIds?.length
    ? data.assigneeUserIds
    : [data.assigneeUserId];

  return {
    ...data,
    currentStatus: normalizeCurrentToDoStatus(data.statusDisplayName ?? ''),
    availableStatuses: normalizeAvailableToDoStatuses(
      data.statusDisplayName ?? '',
      data.applicableStatusTransitions,
    ),
    isAbandoned: data.statusDisplayName === 'Abandoned',
    formattedDate: formatDateTime(data.createdOn ?? ''),
    formattedDueDate: data?.dueDate ? formatDate(data.dueDate) : '',
    ...(
      typeof userDict !== 'undefined'
        ? ({
          assignee: userDict[data.assigneeUserId],
          assignedUsers: assignedUserIds
            .map((userId) => userDict[userId])
            .filter((user): user is UserDTO => typeof user !== 'undefined'),
          collaboratorUsers: (data.collaboratorUserIds ?? [])
            .map((userId) => userDict[userId])
            .filter((user): user is UserDTO => typeof user !== 'undefined'),
          creator: userDict[data.creatorUserId],
        }) : {}
    ),
    ...(
      typeof patientsDict !== 'undefined'
        ? ({
          patient: normalizePatient(patientsDict[data.patientId]),
        }) : {}
    ),
  };
}

export function normalizeToDos(response: ToDoPaginatedCollectionDTO): ToDo[] {
  const patientsDict = response.patients ?? {};
  const usersDict = response.users ?? {};

  return (response?.toDos?.data ?? []).map(
    (toDo) => (
      normalizeToDo(
        toDo as ToDo,
        usersDict,
        patientsDict as Record<string, Patient>,
      )
    ),
  );
}

export function normalizePanelToDo(item: ToDoPanelItemDTO): ToDo {
  return {
    toDoId: item.toDoId ?? 0,
    name: item.name ?? '',
    statusDisplayName: item.statusDisplayName ?? '',
    createdOn: item.createdOn ?? '',
    currentStatus: normalizeCurrentToDoStatus(item.statusDisplayName ?? ''),
    formattedDate: formatDateTime(item.createdOn ?? ''),
  } as ToDo;
}

export function normalizePanelToDos(items: ToDoPanelItemDTO[]): ToDo[] {
  return (items ?? []).map(normalizePanelToDo);
}

export type ToDoFilterMetadataEntry = {
  name: string;
  filterType?: string | null;
  lookupTranslator?: FilterLookupTranslator;
  optionValueMap?: Record<string, string>;
};

export function getLookupOptionFieldValue(
  option: LookupOptionType,
  field?: string | null,
) {
  if (typeof field !== 'string') {
    return undefined;
  }

  const directValue = option?.[field];
  if (!isNil(directValue)) {
    return directValue;
  }

  const { formattedNames } = option ?? {};

  return !isNil(formattedNames) && typeof formattedNames === 'object'
    ? (formattedNames as Record<string, unknown>)?.[field]
    : undefined;
}

export function normalizeLookupOption(
  option: LookupOptionType,
  lookupTranslator?: FilterLookupTranslator,
) {
  if (isNil(lookupTranslator)) {
    return option;
  }

  const fields = [
    lookupTranslator.idField,
    lookupTranslator.displayNameField,
    lookupTranslator.displaySubtitle1Field,
    lookupTranslator.displaySubtitle2Field,
    lookupTranslator.displaySubtitle3Field,
    lookupTranslator.displaySubtitle4Field,
  ];

  return fields.reduce<LookupOptionType>((result, field) => {
    if (typeof field !== 'string') {
      return result;
    }

    const value = getLookupOptionFieldValue(option, field);

    if (!isNil(value)) {
      result[field] = value;
    }

    return result;
  }, {});
}
